import { useEffect, useRef, useState } from "react";
import styles from "./UploadOverlay.module.css";
import Button from "../Button";

const UploadOverlay = ({ percent, loaded, total, onCancel }) => {
  const startTime = useRef(null);
  const prevLoaded = useRef(0);
  const [speed, setSpeed] = useState(0); // Bytes per second
  const [eta, setEta] = useState(null); // ETA in seconds

  useEffect(() => {
    if (loaded === 0) {
      prevLoaded.current = 0;
      startTime.current = null;
      setSpeed(0);
      setEta(null);
      return;
    }

    // Lazy initialization on first byte arrival
    if (!startTime.current) {
      startTime.current = Date.now();
    }

    // Detect 401 token retry sways (uploaded progress decreases)
    if (loaded < prevLoaded.current) {
      startTime.current = Date.now();
    }
    prevLoaded.current = loaded;

    const elapsedSeconds = (Date.now() - startTime.current) / 1000 || 0.1;
    const currentSpeed = loaded / elapsedSeconds;
    setSpeed(currentSpeed);

    const remainingBytes = total - loaded;
    const currentEta = remainingBytes / currentSpeed;
    setEta(currentEta);
  }, [loaded, total]);

  const formatSpeed = (bytesPerSec) => {
    if (bytesPerSec >= 1024 * 1024) {
      return `${(bytesPerSec / (1024 * 1024)).toFixed(2)} MB/s`;
    } else if (bytesPerSec >= 1024) {
      return `${(bytesPerSec / 1024).toFixed(1)} KB/s`;
    }
    return `${Math.round(bytesPerSec)} B/s`;
  };

  const formatEta = (seconds) => {
    if (!isFinite(seconds) || seconds < 0) return "Calculating...";
    if (seconds < 60) return `${Math.round(seconds)} seconds`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins} min ${secs} sec`;
  };

  const loadedMB = (loaded / (1024 * 1024)).toFixed(1);
  const totalMB = (total / (1024 * 1024)).toFixed(1);

  return (
    <div className={styles.overlay}>
      <div className={styles.card}>
        <div className={styles.spinner}></div>
        <div className={styles.progressContainer}>
          <div className={styles.percentage}>{percent}%</div>
          <div className={styles.progressBarOuter}>
            <div className={styles.progressBarInner} style={{ width: `${percent}%` }}></div>
          </div>
        </div>
        <div className={styles.statusText}>
          {percent === 100 ? "Processing on server..." : "Uploading file..."}
        </div>
        {percent < 100 && loaded > 0 && (
          <div className={styles.subText}>
            Uploaded {loadedMB} MB of {totalMB} MB <br />
            Speed: {formatSpeed(speed)} | ETA: {formatEta(eta)}
          </div>
        )}
        <div className={styles.caution}>
          Please do not close this tab or disconnect your internet connection.
        </div>
        <div className={styles.cancelBtn}>
          <Button variant="grey" label="Cancel Upload" onClick={onCancel} />
        </div>
      </div>
    </div>
  );
};

export default UploadOverlay;
